import type { ReactNode } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { ABAS, abaDe, RAIZ_DE_RELATORIOS } from '@/features/relatorios/navegacao';
import { BarraDeFiltro, ProvedorDeFiltro } from '@/features/relatorios/filtros';

/**
 * Casca dos relatórios: título, abas e o recorte compartilhado.
 *
 * As abas são **rotas**, não estado de componente — pelo mesmo motivo da
 * administração: `/relatorios/ans` precisa ser um endereço que se manda por
 * mensagem, se recarrega e se alcança pelo botão "voltar".
 *
 * O filtro, ao contrário, é estado da casca: ele atravessa as abas de
 * propósito, e o provedor mora aqui para que trocar de aba não zere o período
 * que a pessoa acabou de escolher.
 */
export function RelatoriosLayout({ children }: { children?: ReactNode }) {
  const pathname = useRouterState({ select: (estado) => estado.location.pathname });
  const atual = abaDe(pathname.split('/').filter(Boolean).at(1) ?? '');

  return (
    <ProvedorDeFiltro>
      <div className="mx-auto max-w-[1400px] space-y-4">
        <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
          <div>
            <h1 className="text-base font-semibold">Relatórios</h1>
            {atual ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{atual.resumo}</p>
            ) : null}
          </div>
        </header>

        <nav aria-label="Relatórios" className="border-b border-border">
          <ul className="-mb-px flex flex-wrap gap-1">
            {ABAS.map((aba) => {
              const destino = `${RAIZ_DE_RELATORIOS}/${aba.caminho}`;
              const ativa = pathname === destino || pathname.startsWith(`${destino}/`);
              const Icone = aba.icone;
              return (
                <li key={aba.caminho}>
                  <Link
                    to={destino}
                    aria-current={ativa ? 'page' : undefined}
                    className={cn(
                      'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium',
                      'transition-colors focus-visible:shadow-glow focus-visible:outline-none',
                      ativa
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                    )}
                  >
                    <Icone className="h-4 w-4" aria-hidden />
                    {aba.rotulo}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {atual ? <p className="max-w-3xl text-sm text-muted-foreground">{atual.resumo}</p> : null}

        <BarraDeFiltro />

        {children ?? <Outlet />}
      </div>
    </ProvedorDeFiltro>
  );
}
